import { Comparable } from '../comparable'
import { DateObjectUnits, DateTime, DurationUnit } from 'luxon'
import { DateFormat, LocalizedDateFormat, LocalizedDateTimeFormat, TimeFormat } from './model/date'
import { DEFAULT_LOCALE, DEFAULT_TIMEZONE } from '../constants'

export abstract class Temporal extends Comparable<Temporal> {
	public constructor(date: DateTime) {
		super()
		this._date = date
	}

	protected static TIMEZONE = DEFAULT_TIMEZONE
	protected static API_TIMEZONE = 'UTC'
	protected _date: DateTime

	protected static _splitTime(time: string): DateObjectUnits {
		const [hour, minute, second, millisecond] = time.split(':').map(element => element ? Number(element) : 0)
		return {
			hour: hour ?? 0,
			minute: minute ?? 0,
			second: second ?? 0,
			millisecond: millisecond ?? 0
		}
	}

	public abstract format(): string;

	protected toDisplayDate(format: LocalizedDateTimeFormat, locale?: string): string {
		const date = locale ? this._date.setLocale(locale).setZone(Temporal.TIMEZONE) : this._date.setLocale(DEFAULT_LOCALE).setZone(Temporal.TIMEZONE)
		switch (format) {
			case DateFormat.SHORT_DATE_2_DIGITS_ONLY_DAY:
				return date.toLocaleString({ day: '2-digit' })
			case DateFormat.REVERSE_SHORT_DATE:
				return date.toFormat('yyyy/MM/dd')
			case DateFormat.SHORT_DATE_WITH_DASH:
				return date.toFormat('yyyy-MM-dd')
			case DateFormat.SHORT_DATE_2_DIGITS_2_MONTH:
				return date.toLocaleString({ day: '2-digit', month: '2-digit' })
			case LocalizedDateFormat.LONG_DATE:
				return date.toFormat('cccc dd LLLL yyyy')
			case LocalizedDateFormat.LONG_DATE_WITHOUT_YEAR_STRING:
				return date.toFormat('cccc dd/LL')
			case DateFormat.SHORT_DATE_2_DIGITS:
				return date.toLocaleString({ day: '2-digit', month: '2-digit', year: '2-digit' })
			case DateFormat.SHORT_DATE:
				return date.toLocaleString(DateTime.DATE_SHORT)
			case LocalizedDateFormat.LONG_DATE_WITH_WEEKDAY:
				return date.toFormat('cccc dd LLLL yyyy')
			case LocalizedDateFormat.LONG_DATE_WITHOUT_YEAR:
				return date.toFormat('cccc dd LLLL')
			case DateFormat.SHORT_DATETIME:
				return date.toLocaleString(DateTime.DATETIME_SHORT)
			case DateFormat.DATETIME_SHORT_WITH_SECONDS:
				return date.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
			case TimeFormat.TIME_24_SIMPLE:
				return date.toLocaleString(DateTime.TIME_24_SIMPLE)
			case TimeFormat.TIME_24_WITH_SECONDS:
				return date.toLocaleString(DateTime.TIME_24_WITH_SECONDS)
			case TimeFormat.TIME_ONLY:
				return date.toLocaleString(DateTime.TIME_SIMPLE).replace(':', 'h')
			case LocalizedDateFormat.WEEKDAY_ONLY:
				return date.toLocaleString({ weekday: 'long' })
			case LocalizedDateFormat.DAY_AND_MONTH:
				return date.toLocaleString({ day: '2-digit', month: 'long' })
			default:
				return date.toFormat('yyyy/MM/dd')
		}
	}

	_compareTo(to: Temporal): number {
		if (+this._date === +to._date) {
			return 0
		}
		return this._date < to._date ? -1 : 1
	}

	_diff = (to: Temporal, unit: DurationUnit) => {
		return this._date.diff(to._date, unit).get(unit)
	}

	protected _safe(dateTime: DateTime<boolean>): DateTime<true> {
		if (dateTime.invalidReason) {
			console.warn('Invalid datetime provided. Explanation:', dateTime.invalidExplanation)
			throw Error(dateTime.invalidReason)
		}
		return dateTime as DateTime<true>
	}

	public toString(): string {
		return this.format()
	}

	public abstract toJSON(): string;
}
